import { ChangeEvent, useEffect } from 'react';
import { Link } from '@mui/material';
import { CoreBox, CoreCheckbox, CoreFormControlLabel, CoreTextField, CoreTypography } from '@youscience/core';
import {
  EarlyDecisionAgreementDocument,
  EarlyDecisionAgreementSchema,
  YesNo,
} from '@youscience/commonapp-service-common';

import { TabSections } from '@interfaces/recommenderWorkspace';

import SectionAccordion from '@components/SectionAccordion';

import { FormState, FormStateErrors } from '@constants/commonApp/types';
import { useApplicantFormSection } from '@pages/StudentDetailsPage/hooks/useApplicantFormSection';

import { sxStyles } from '../../EarlyDecisionAgreement.styles';
import { CURRENT_YEAR } from '@constants';
import MonthDayYearDatePicker from '@components/MonthDayYearDatePicker';

const MAX_SIGNATURE_LENGTH = 60;
const INVALID_DATE_RANGE_MESSAGE = `Year must be between ${CURRENT_YEAR} and ${CURRENT_YEAR + 1}.`;

export type AgreementFields = Partial<EarlyDecisionAgreementDocument[TabSections.Agreement]>;

interface AgreementProps {
  agreement?: AgreementFields;
  onFormChange: (formState: FormState<AgreementFields>) => void;
  onExpand: () => void;
  isExtended: boolean;
  isSaved: boolean;
}

const validator = EarlyDecisionAgreementSchema.shape.agreement;

export const Agreement = ({ agreement = {}, onFormChange, onExpand, isExtended, isSaved }: AgreementProps) => {
  const [formState, setFormStateWithErrors] = useApplicantFormSection<AgreementFields, typeof validator>(
    agreement,
    validator,
    isSaved,
    onFormChange,
  );

  const handleCheckBoxChange = (event: ChangeEvent<HTMLInputElement>, checked: boolean) => {
    setFormStateWithErrors({ [event.target.name]: checked ? YesNo.Yes : undefined });
  };

  useEffect(() => {
    if (!formState.data.date) return;

    const initialDateYear = new Date(formState.data.date).getFullYear();

    if (initialDateYear < CURRENT_YEAR || initialDateYear > CURRENT_YEAR + 1) {
      setFormStateWithErrors({}, { date: INVALID_DATE_RANGE_MESSAGE } as FormStateErrors<AgreementFields>);
    }
  }, []);

  const handleDateChange =
    (fieldName: string, { optional = false } = {}) =>
    (value: Date | null) => {
      if (value) {
        setFormStateWithErrors({
          [fieldName]: `${`${value.getMonth() + 1}`.padStart(2, '0')}/${`${value.getDate()}`.padStart(2, '0')}/${`${value.getFullYear()}`.padStart(4, '0')}`,
        });
      } else {
        setFormStateWithErrors({ [fieldName]: optional ? null : undefined });
      }
    };

  const handleDateError = (fieldName: string, error: string | undefined) => {
    let errorText = '';

    if (error === 'minDate' || error === 'maxDate') {
      errorText = INVALID_DATE_RANGE_MESSAGE;
    } else if (error === 'invalidDate') {
      errorText = 'Please enter a valid date';
    }

    setFormStateWithErrors({}, { [fieldName as keyof AgreementFields]: errorText } as FormStateErrors<AgreementFields>);
  };

  const handleTextFieldChange =
    ({ optional = false } = {}) =>
    (event: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
      setFormStateWithErrors({ [event.target.name]: event.target.value || (optional ? null : undefined) });
    };

  return (
    <SectionAccordion
      title="Agreement"
      isExpanded={isExtended}
      onChange={onExpand}
      isChanged={formState.hasChanged}
      showCheckmark={formState.isSaved && !!formState.isFormValid}
    >
      <CoreBox sx={sxStyles.accordionDetails}>
        <CoreTypography>
          If the student is accepted under an Early Decision plan, the student must promptly withdraw the applications
          submitted to other colleges and universities and make no additional applications to any other university in
          any country.
        </CoreTypography>

        <CoreTypography>
          If the student is an Early Decision candidate and is seeking financial aid, the student need not withdraw
          other applications until the student has received notification about financial aid from the admitting Early
          Decision institution.
        </CoreTypography>

        <CoreBox>
          <CoreTypography>
            For more information about the Early Decision plan, please refer to{' '}
            <Link sx={sxStyles.link} href="https://www.commonapp.org/about/guiding-principles" target="_blank">
              Common App's Guiding Principles, Opens in new tab.
            </Link>
          </CoreTypography>

          <CoreFormControlLabel
            control={
              <CoreCheckbox
                data-testid="affirmation"
                name="affirmation"
                checked={formState.data?.affirmation === YesNo.Yes}
                onChange={handleCheckBoxChange}
              />
            }
            label={
              <CoreTypography variant="body2">
                As the counselor, I have advised the student to abide by the Early Decision commitment outlined above.
              </CoreTypography>
            }
          />
        </CoreBox>

        {formState.data?.affirmation ? (
          <CoreBox sx={sxStyles.flexWrap}>
            <CoreTextField
              name="signature"
              size="medium"
              fullWidth
              value={formState.data?.signature}
              label="Signature"
              required
              multiline
              helperText={
                formState.errors.signature || `${formState?.data?.signature?.length ?? 0}/${MAX_SIGNATURE_LENGTH}`
              }
              inputProps={{ maxLength: MAX_SIGNATURE_LENGTH }}
              onChange={handleTextFieldChange({ optional: true })}
              error={!!formState.errors.signature}
            />

            <MonthDayYearDatePicker
              name="date"
              label="Date"
              value={(formState?.data?.date as string) ?? undefined}
              required
              slotProps={{
                inputProps: { clearable: true, onClear: () => handleDateChange('date') },
                error: !!formState.errors.date,
              }}
              handleDateChange={handleDateChange('date', { optional: true })}
              minDate={new Date(CURRENT_YEAR, 0)}
              maxDate={new Date(CURRENT_YEAR + 1, 11, 31)}
              handleDateError={handleDateError}
              error={formState.errors.date}
            />
          </CoreBox>
        ) : null}
      </CoreBox>
    </SectionAccordion>
  );
};
